import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import './ScannerHistoryView.css';
import './AdminMoreDetailPages.css';

function ScannerHistoryView({ scannerData }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedLog, setSelectedLog] = useState(null);

  const loadHistory = useCallback(async () => {
    if (!scannerData?.uid) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const q = query(
        collection(db, 'studentActivityLogs'),
        where('scannerId', '==', scannerData.uid),
        orderBy('timestamp', 'desc')
      );
      const snapshot = await getDocs(q);
      const items = [];
      snapshot.forEach((d) => {
        items.push({ id: d.id, ...d.data() });
      });
      setLogs(items);
    } catch (error) {
      console.error('Error loading scan history:', error);
    } finally {
      setLoading(false);
    }
  }, [scannerData?.uid]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const getStartOfToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };

  const getStartOfWeek = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };

  const filteredLogs = logs.filter((log) => {
    if (filter === 'all') return true;
    const ts = log.timestamp;
    let ms = null;
    if (ts && typeof ts.toMillis === 'function') {
      ms = ts.toMillis();
    } else if (ts && ts.seconds) {
      ms = ts.seconds * 1000;
    } else if (ts instanceof Date) {
      ms = ts.getTime();
    }
    if (ms == null) return filter === 'all';
    if (filter === 'today') return ms >= getStartOfToday();
    if (filter === 'week') return ms >= getStartOfWeek();
    return true;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    let date;
    if (timestamp && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    } else if (timestamp && timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      return '—';
    }
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="scanner-history-view">
        <h2>Scan History</h2>
        <div className="loading">Loading history...</div>
      </div>
    );
  }

  // === Full-screen scan detail page ===
  if (selectedLog) {
    const log = selectedLog;
    console.log('🔥 ScannerHistory: Showing detail for log', log.id, log);
    return (
      <div className="scanner-history-view detail-page">
        <button className="detail-back-button" onClick={() => setSelectedLog(null)}>
          ◄ Back to History
        </button>
        <div className="detail-card">
          <div className="detail-header">
            <div>
              <h2>{log.studentName || 'Unknown student'}</h2>
              <p className="detail-subtitle">{log.activityType || 'Activity'}</p>
            </div>
            <span className="detail-status-badge" style={{ background: '#dcfce7', color: '#15803d' }}>
              {log.activityType || 'activity'}
            </span>
          </div>

          <div className="detail-grid">
            <div className="detail-field">
              <span className="detail-label">Student</span>
              <span className="detail-value">{log.studentName || 'Unknown'}</span>
            </div>
            {log.studentId && (
              <div className="detail-field">
                <span className="detail-label">Student ID</span>
                <span className="detail-value">{log.studentId}</span>
              </div>
            )}
            <div className="detail-field">
              <span className="detail-label">Activity Type</span>
              <span className="detail-value">{log.activityType || 'N/A'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Location</span>
              <span className="detail-value">📍 {log.locationName || 'Unknown location'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Scanner</span>
              <span className="detail-value">{log.scannerName || log.scannerId || 'N/A'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Time</span>
              <span className="detail-value">{formatDate(log.timestamp)}</span>
            </div>
            {(Number.isFinite(log.latitude) || Number.isFinite(log.longitude)) && (
              <div className="detail-field">
                <span className="detail-label">Coordinates</span>
                <span className="detail-value">{log.latitude ?? '—'}, {log.longitude ?? '—'}</span>
              </div>
            )}
          </div>

          {log.notes && (
            <div className="detail-section">
              <h4>Notes</h4>
              <p className="detail-text">{log.notes}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="scanner-history-view">
      <h2>Scan History</h2>

      <div className="filter-bar">
        <button
          className={`filter-button ${filter === 'today' ? 'active' : ''}`}
          onClick={() => setFilter('today')}
        >
          Today
        </button>
        <button
          className={`filter-button ${filter === 'week' ? 'active' : ''}`}
          onClick={() => setFilter('week')}
        >
          This Week
        </button>
        <button
          className={`filter-button ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
      </div>

      {filteredLogs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No scan history</h3>
          <p>
            {logs.length === 0
              ? 'Activities you log will appear here.'
              : 'No logs match the selected filter.'}
          </p>
        </div>
      ) : (
        <div className="history-list">
          {filteredLogs.map((log) => (
            <div
              key={log.id}
              className="history-card"
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedLog(log)}
            >
              <div className="history-card-header">
                <h3>{log.studentName || 'Unknown student'}</h3>
                <span className="activity-badge">{log.activityType || 'Activity'}</span>
              </div>
              <div className="history-card-meta">
                <span className="meta-item">📍 {log.locationName || 'Unknown location'}</span>
                <span className="meta-item">🕐 {formatDate(log.timestamp)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ScannerHistoryView;
